
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';

interface PreferenceSetting {
  id: string;
  title: string;
  description: string;
  enabled: boolean;
}

const Preferences: React.FC = () => {
  const { toast } = useToast();
  
  // Mock preferences - replace with API call in production
  const [preferences, setPreferences] = useState<PreferenceSetting[]>([
    {
      id: 'email-notifications',
      title: 'Email Notifications',
      description: 'Receive email notifications about your upcoming plant care visits',
      enabled: true
    },
    {
      id: 'text-notifications',
      title: 'Text Notifications',
      description: 'Receive text messages about your upcoming plant care visits',
      enabled: false
    },
    {
      id: 'calendar-sync',
      title: 'Calendar Sync',
      description: 'Automatically add plant care visits to your calendar',
      enabled: true
    },
    {
      id: 'plant-tips',
      title: 'Plant Care Tips',
      description: 'Receive weekly tips about plant care and maintenance',
      enabled: true
    },
    {
      id: 'technician-photos',
      title: 'Technician Photos',
      description: 'Allow technicians to take photos of your plants to track their progress',
      enabled: true
    }
  ]);

  const handleTogglePreference = (id: string) => {
    setPreferences(prev => 
      prev.map(pref => 
        pref.id === id ? { ...pref, enabled: !pref.enabled } : pref
      )
    );
  };

  const handleSavePreferences = () => {
    // In a real app, this would save the preferences to the database
    toast({
      title: "Preferences Saved",
      description: "Your preferences have been updated successfully."
    });
  };

  return (
    <div className="min-h-screen bg-white py-8">
      <div className="container max-w-4xl mx-auto px-4">
        <Card className="border-front-gray-200 bg-white shadow-sm">
          <CardHeader className="pb-6">
            <CardTitle className="text-2xl font-bold text-front-gray-900">Preferences</CardTitle>
            <CardDescription className="text-front-gray-600 mt-2">
              Customize your plant care experience and notification settings
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-8">
              {preferences.map((pref, index) => (
                <div key={pref.id}>
                  <div className="flex items-center justify-between py-4">
                    <div className="space-y-2 flex-1">
                      <Label 
                        htmlFor={pref.id} 
                        className="text-base font-semibold text-front-gray-900 cursor-pointer"
                      >
                        {pref.title}
                      </Label>
                      <p className="text-sm text-front-gray-600 leading-relaxed">
                        {pref.description}
                      </p>
                    </div>
                    <Switch 
                      id={pref.id} 
                      checked={pref.enabled} 
                      onCheckedChange={() => handleTogglePreference(pref.id)}
                      className="ml-6"
                    />
                  </div>
                  
                  {index < preferences.length - 1 && (
                    <Separator className="bg-front-gray-200" />
                  )}
                </div>
              ))}
            </div>
          </CardContent>
          <CardFooter className="pt-6 border-t border-front-gray-200">
            <Button 
              onClick={handleSavePreferences} 
              className="ml-auto bg-front-gray-900 hover:bg-front-gray-800 text-white px-8 font-semibold"
            >
              Save Preferences
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default Preferences;
